/**
 * Widget ↔ metric-category compatibility (leaf module).
 *
 * `WIDGET_METRIC_COMPATIBILITY` is the category-axis allowlist: which metric
 * CATEGORIES each widget type accepts (`["*"]` = all). It is the fallback axis
 * of the dual-axis metric filter (see `metricCompatibility.ts`) and the axis
 * the config-panel `MetricSelector` uses.
 *
 * Extracted from `DataSourceSection.tsx` (Story 37.7) into this leaf module so
 * `lib/metricCompatibility.ts` can consume it WITHOUT importing a component
 * (which would invert the `components → lib` layering and pull the whole
 * DataSourceSection React subtree into the lib import graph). `DataSourceSection`
 * re-exports it for backward compatibility, so every existing
 * `from "./DataSourceSection"` importer keeps working — values unchanged.
 */

/**
 * Coerce a widget's metric binding into a flat list of metric-KEY strings,
 * tolerating EVERY shape it can arrive in:
 *   - `config.metrics: string[]`                       (line/bar renderer + auto-bind)
 *   - `config.metrics: {key,label,integrationId}[]`    (presets + config-panel history)
 *   - `config.metricKey: string`                       (kpi/pie single-metric)
 *
 * Why this exists: the chart renderers AND the mock-data generators both read
 * `config.metrics` and historically assumed `string[]`. A preset (or panel edit)
 * that stored the OBJECT shape produced keys like "[object Object]" — so the
 * series never matched the data and the chart rendered empty ("No data"). This
 * is the single normalizer both paths use so every shape renders.
 */
export function metricKeysFromConfig(config: Record<string, unknown> | undefined): string[] {
  const raw = config?.metrics;
  if (Array.isArray(raw) && raw.length > 0) {
    const keys = raw
      .map((m) => {
        if (typeof m === "string") return m;
        if (m && typeof m === "object" && typeof (m as { key?: unknown }).key === "string") {
          return (m as { key: string }).key;
        }
        return "";
      })
      .filter((k): k is string => k.length > 0);
    if (keys.length > 0) return keys;
  }
  const single = config?.metricKey;
  return typeof single === "string" && single.length > 0 ? [single] : [];
}

/**
 * True when a widget config carries an actual data-source binding — at least one
 * metric key (any shape), a `columns` list (data-table), or an `integrationId`.
 * An UNBOUND data widget (e.g. a Line Chart just dropped from the palette) can
 * never resolve live data, so the canvas should preview it with SAMPLE data
 * instead of forwarding it to the live path (which fails with
 * "Unable to determine data source for this widget").
 */
export function widgetHasDataBinding(config: Record<string, unknown> | undefined): boolean {
  if (!config) return false;
  if (metricKeysFromConfig(config).length > 0) return true;
  if (Array.isArray(config.columns) && config.columns.some((c) => typeof c === "string" && c.length > 0)) return true;
  // top-posts binds via `platform`, not metrics.
  if (typeof config.platform === "string" && config.platform.length > 0 && config.platform !== "all") return true;
  return false;
}

/** Which metric categories are compatible with which widget types (lowercase for comparison). */
export const WIDGET_METRIC_COMPATIBILITY: Record<string, string[]> = {
  kpi: ["*"],
  "line-chart": ["*"],
  "bar-chart": ["*"],
  "pie-chart": ["*"],
  "data-table": ["*"],
  gauge: ["*"],
  funnel: ["conversion", "engagement"],
  goal: ["*"],
  benchmark: ["*"],
  "custom-metric": ["*"],
  "top-posts": ["content", "engagement", "video"],
  demographic: ["demo", "audience"],
};
